import React, { useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Eraser, Save, PenLine, CheckCircle2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-SG', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch {
    return iso;
  }
};

/**
 * Renders one signature card — either captured (shows image + name + date)
 * or capture mode (shows pad + name input + Save/Clear buttons).
 */
const SignatureCard = ({
  title,
  testidPrefix,
  signature,
  name,
  signedAt,
  defaultName,
  onSave,
  onClear,
  onAmendDate,
  saving,
  showLorry,
  defaultLorry,
  capturedLorry,
}) => {
  const padRef = useRef(null);
  const [lorryNo, setLorryNo] = useState(defaultLorry || '');
  const [editingName, setEditingName] = useState(defaultName || '');
  const [isCapturing, setIsCapturing] = useState(false);
  const [editingDate, setEditingDate] = useState(false);
  const [newDate, setNewDate] = useState('');

  const handleClearPad = () => padRef.current?.clear();

  const handleSave = async () => {
    if (!padRef.current || padRef.current.isEmpty()) {
      toast.error('Please sign before saving');
      return;
    }
    if (!editingName.trim()) {
      toast.error('Please enter the signatory name');
      return;
    }
    let dataUrl;
    try {
      dataUrl = padRef.current.getTrimmedCanvas().toDataURL('image/png');
    } catch (err) {
      try {
        dataUrl = padRef.current.toDataURL('image/png');
      } catch (err2) {
        toast.error('Could not capture signature. Please clear and try again.');
        console.error('Signature capture failed:', err, err2);
        return;
      }
    }
    if (!dataUrl || dataUrl.length < 100) {
      toast.error('Signature appears empty. Please try again.');
      return;
    }
    await onSave(dataUrl, editingName.trim(), lorryNo.trim());
    setIsCapturing(false);
  };

  // Captured state
  if (signature && !isCapturing) {
    return (
      <Card className="border-green-200 bg-green-50/40">
        <CardContent className="p-4">
          <div className="flex justify-between items-center mb-3">
            <h4 className="font-semibold text-gray-800 flex items-center gap-2">
              <CheckCircle2 size={16} className="text-green-600" /> {title}
            </h4>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClear}
              disabled={saving}
              data-testid={`${testidPrefix}-reset`}
            >
              <RotateCcw size={14} className="mr-1" /> Reset
            </Button>
          </div>
          <div className="bg-white border rounded-md p-2 mb-3 flex justify-center">
            <img
              src={signature}
              alt={`${title} signature`}
              className="max-h-24 object-contain"
              data-testid={`${testidPrefix}-image`}
            />
          </div>
          <p className="text-sm text-gray-700"><span className="text-gray-500">Name:</span> <span className="font-medium">{name || defaultName || '—'}</span></p>
          {showLorry && capturedLorry && <p className="text-sm text-gray-700"><span className="text-gray-500">Lorry No:</span> <span className="font-medium">{capturedLorry}</span></p>}
          <div className="text-sm text-gray-700 flex items-center gap-2 flex-wrap">
            <span><span className="text-gray-500">Signed at:</span> {fmtDate(signedAt)}</span>
            {!editingDate ? (
              <button
                type="button"
                onClick={() => {
                  setNewDate(signedAt ? new Date(signedAt).toISOString().slice(0, 10) : '');
                  setEditingDate(true);
                }}
                className="text-xs text-orange-600 hover:text-orange-700 underline"
                data-testid={`${testidPrefix}-amend-date-btn`}
              >
                Amend date
              </button>
            ) : (
              <span className="flex items-center gap-1">
                <Input
                  type="date"
                  value={newDate}
                  onChange={(e) => setNewDate(e.target.value)}
                  className="h-7 w-36 text-xs"
                  data-testid={`${testidPrefix}-amend-date-input`}
                />
                <Button
                  size="sm"
                  className="h-7 px-2 bg-orange-600 hover:bg-orange-700 text-xs"
                  disabled={saving || !newDate}
                  onClick={async () => {
                    await onAmendDate(newDate);
                    setEditingDate(false);
                  }}
                  data-testid={`${testidPrefix}-amend-date-save`}
                >
                  Save
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-xs"
                  onClick={() => setEditingDate(false)}
                >
                  Cancel
                </Button>
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Capture state
  return (
    <Card className="border-dashed border-2 border-gray-300">
      <CardContent className="p-4">
        <h4 className="font-semibold text-gray-800 flex items-center gap-2 mb-3">
          <PenLine size={16} className="text-orange-600" /> {title}
        </h4>
        <div className="mb-3">
          <Label htmlFor={`${testidPrefix}-name`} className="text-sm">Name *</Label>
          <Input
            id={`${testidPrefix}-name`}
            value={editingName}
            onChange={(e) => setEditingName(e.target.value)}
            placeholder="Full name of signatory"
            className="mt-1"
            data-testid={`${testidPrefix}-name-input`}
          />
        </div>
        {showLorry && (
          <div className="mb-3">
            <Label className="text-sm">Lorry Number</Label>
            <Input
              value={lorryNo}
              onChange={(e) => setLorryNo(e.target.value)}
              placeholder="e.g. XD1234A"
              className="mt-1"
              data-testid={`${testidPrefix}-lorry-input`}
            />
          </div>
        )}
        <Label className="text-sm">Sign in the box below</Label>
        <div className="border-2 border-gray-300 rounded-md bg-white mt-1 mb-3 touch-none">
          <SignatureCanvas
            ref={padRef}
            penColor="#1f2937"
            clearOnResize={false}
            canvasProps={{
              className: 'w-full h-32 rounded-md',
              'data-testid': `${testidPrefix}-pad`,
            }}
          />
        </div>
        <div className="flex gap-2 justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={handleClearPad}
            disabled={saving}
            data-testid={`${testidPrefix}-clear`}
          >
            <Eraser size={14} className="mr-1" /> Clear
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving}
            className="bg-orange-600 hover:bg-orange-700"
            data-testid={`${testidPrefix}-save`}
          >
            <Save size={14} className="mr-1" /> {saving ? 'Saving...' : 'Save signature'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * SignaturePanel — handles capture/display of driver + customer signatures
 * for a Delivery Order. Calls back onUpdate(updatedDeliveryOrder) when saved.
 */
export const SignaturePanel = ({ deliveryOrder, onUpdate }) => {
  const [saving, setSaving] = useState(false);

  const saveSignature = async (role, dataUrl, signedName, lorryNo) => {
    setSaving(true);
    try {
      const payload =
        role === 'driver'
          ? { driver_signature: dataUrl, driver_signature_name: signedName, driver_lorry_no: lorryNo || '' }
          : { customer_signature: dataUrl, customer_signature_name: signedName };

      await axios.patch(
        `${BACKEND_URL}/api/delivery-orders/${deliveryOrder.id}/signature`,
        payload
      );
      toast.success(`${role === 'driver' ? 'Driver' : 'Customer'} signature saved`);
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save signature');
    } finally {
      setSaving(false);
    }
  };

  const amendSignedDate = async (role, dateStr) => {
    setSaving(true);
    try {
      const payload = role === 'driver' ? { driver_signed_at: dateStr } : { customer_signed_at: dateStr };
      await axios.patch(`${BACKEND_URL}/api/delivery-orders/${deliveryOrder.id}/signature`, payload);
      toast.success('Signed date updated');
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update signed date');
    } finally {
      setSaving(false);
    }
  };

  const clearSignature = async (role) => {
    if (!window.confirm(`Clear the ${role} signature and re-capture?`)) return;
    setSaving(true);
    try {
      const payload =
        role === 'driver'
          ? { driver_signature: '', driver_signature_name: '' }
          : { customer_signature: '', customer_signature_name: '' };
      await axios.patch(
        `${BACKEND_URL}/api/delivery-orders/${deliveryOrder.id}/signature`,
        payload
      );
      onUpdate?.();
    } catch (e) {
      toast.error('Failed to reset signature');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-t pt-6 mt-6 print:hidden" data-testid="signature-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
          <PenLine size={20} className="text-orange-600" /> E-Signatures
        </h3>
        <span className="text-xs text-gray-500">
          Tap and sign on a touchscreen, or use your mouse.
        </span>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <SignatureCard
          title="Driver / Delivered By"
          testidPrefix="driver-signature"
          signature={deliveryOrder.driver_signature}
          name={deliveryOrder.driver_signature_name}
          signedAt={deliveryOrder.driver_signed_at}
          defaultName={deliveryOrder.driver_name}
          showLorry
          defaultLorry={deliveryOrder.driver_lorry_no || deliveryOrder.vehicle_number}
          capturedLorry={deliveryOrder.driver_lorry_no}
          onSave={(url, n, lorry) => saveSignature('driver', url, n, lorry)}
          onClear={() => clearSignature('driver')}
          onAmendDate={(d) => amendSignedDate('driver', d)}
          saving={saving}
        />
        <SignatureCard
          title="Received By Customer"
          testidPrefix="customer-signature"
          signature={deliveryOrder.customer_signature}
          name={deliveryOrder.customer_signature_name}
          signedAt={deliveryOrder.customer_signed_at}
          defaultName=""
          onSave={(url, n) => saveSignature('customer', url, n)}
          onClear={() => clearSignature('customer')}
          onAmendDate={(d) => amendSignedDate('customer', d)}
          saving={saving}
        />
      </div>
    </div>
  );
};

export default SignaturePanel;
